import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_GET_PROJECT_UTILIZATION_REPORT } from "../../../config/Endpoints.js";
import type { ProjectUtilizationReport } from "../../components/features/project-reports/types.ts";
import { postRawJson } from "../../utils/api.ts";

function unwrapDetails(raw: unknown): ProjectUtilizationReport {
  if (raw && typeof raw === "object" && "data" in raw) {
    return (raw as { data: ProjectUtilizationReport }).data;
  }
  return raw as ProjectUtilizationReport;
}

export function useProjectDetails(projectId: string) {
  const [data, setData] = useState<ProjectUtilizationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accessToken = Cookies.get("accessToken");

  const fetchDetails = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) {
      setData(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await postRawJson<unknown>(
        API_GET_PROJECT_UTILIZATION_REPORT,
        accessToken,
        { projectId: Number(projectId), fromDate: "1900-01-01", toDate: "2100-12-31" },
        signal,
      );
      if (!signal?.aborted) setData(unwrapDetails(response.data));
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) {
        setError(e instanceof Error ? e.message : "Failed to load project details");
        setData(null);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [accessToken, projectId]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchDetails(controller.signal);
    return () => controller.abort();
  }, [fetchDetails]);

  return { data, loading, error };
}
